export interface GeoPoint {
  latitude: number;
  longitude: number;
  speed: number;
  deviceId?: string;
}

export interface ColoredRun {
  positions: [number, number][];
  color: string;
  deviceId: string;
}

export const UNKNOWN_DEVICE_KEY = "__unknown__";

/** Min and max speed without spreading the array into arguments, which throws a RangeError at ~100k points. */
export function getSpeedRange(points: Pick<GeoPoint, "speed">[]): { min: number; max: number } {
  if (points.length === 0) return { min: 0, max: 0 };
  let min = Infinity;
  let max = -Infinity;
  for (const point of points) {
    if (point.speed < min) min = point.speed;
    if (point.speed > max) max = point.speed;
  }
  return { min, max };
}

export interface RangeLike {
  /** Without a device id the range matches the points of every vehicle. */
  deviceId?: string;
  startMs: number;
  stopMs: number;
}

interface IndexedRange {
  index: number;
  startMs: number;
  stopMs: number;
}

/** Last range (sorted by start) that starts at or before `ts` and still contains it, else -1. */
function findContainingRange(sorted: IndexedRange[], ts: number): number {
  let low = 0;
  let high = sorted.length - 1;
  let candidate = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (sorted[mid].startMs <= ts) {
      candidate = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  // Walk back over overlapping ranges until one contains the time.
  for (let i = candidate; i >= 0; i -= 1) {
    if (ts <= sorted[i].stopMs) return sorted[i].index;
  }
  return -1;
}

/**
 * Sorts every point into the trip range of its vehicle that contains its time (for overlapping trips, the one that starts latest), in one pass
 * (binary search per point instead of testing every range). Result index = range index; points
 * outside every range are dropped, input order is kept inside a range.
 */
export function assignPointsToRanges<P extends { dateTime: Date; deviceId?: string }>(
  points: P[],
  ranges: RangeLike[],
): P[][] {
  const result: P[][] = ranges.map(() => []);
  const byDevice = new Map<string, IndexedRange[]>();
  const anyDevice: IndexedRange[] = [];
  ranges.forEach((range, index) => {
    const entry = { index, startMs: range.startMs, stopMs: range.stopMs };
    if (range.deviceId) {
      const list = byDevice.get(range.deviceId);
      if (list) list.push(entry);
      else byDevice.set(range.deviceId, [entry]);
    } else {
      anyDevice.push(entry);
    }
  });
  const byStart = (a: IndexedRange, b: IndexedRange) => a.startMs - b.startMs;
  byDevice.forEach(list => list.sort(byStart));
  anyDevice.sort(byStart);

  for (const point of points) {
    const ts = point.dateTime.getTime();
    if (!Number.isFinite(ts)) continue;
    let index = point.deviceId ? findContainingRange(byDevice.get(point.deviceId) ?? [], ts) : -1;
    if (index < 0) index = findContainingRange(anyDevice, ts);
    if (index >= 0) result[index].push(point);
  }
  return result;
}

/** The time-ordered points of one trip (or of one vehicle when no trips are known). */
export interface PointGroup<P extends GeoPoint = GeoPoint> {
  deviceId: string;
  points: P[];
}

/**
 * Connects the points of each group into as few polylines as possible: consecutive segments of the
 * same colour share one run. Groups are never joined, so two trips of one vehicle stay two lines.
 * A run starts at the end point of the previous run, so the line stays unbroken.
 */
export function buildColoredRuns(
  groups: PointGroup[],
  colorFor: (deviceId: string, segmentSpeed: number) => string,
): ColoredRun[] {
  const runs: ColoredRun[] = [];
  for (const { deviceId, points } of groups) {
    let current: ColoredRun | null = null;
    for (let i = 1; i < points.length; i += 1) {
      const start = points[i - 1];
      const end = points[i];
      const color = colorFor(deviceId, (start.speed + end.speed) / 2);
      if (!current || current.color !== color) {
        current = { positions: [[start.latitude, start.longitude]], color, deviceId };
        runs.push(current);
      }
      current.positions.push([end.latitude, end.longitude]);
    }
  }
  return runs;
}

/**
 * Index of the point closest to `target` (in screen space via `project`), or -1 when none is within
 * `maxDistancePx`. One pass; used for a single map click handler instead of a handler per segment.
 */
export function findNearestPointIndex<P extends GeoPoint>(
  points: P[],
  target: { x: number; y: number },
  project: (point: P) => { x: number; y: number },
  maxDistancePx: number,
): number {
  let best = -1;
  let bestDistance = maxDistancePx * maxDistancePx;
  for (let i = 0; i < points.length; i += 1) {
    const { x, y } = project(points[i]);
    const distance = (x - target.x) ** 2 + (y - target.y) ** 2;
    if (distance <= bestDistance) {
      bestDistance = distance;
      best = i;
    }
  }
  return best;
}
